import React, { useState, useCallback, useEffect } from 'react';
import Layout from '../components/Layout';
import { FaClipboardList, FaSearch, FaUserCheck, FaUserTimes, FaCrosshairs, FaCampground, FaUsers } from 'react-icons/fa';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

/** Returns a Tailwind text color class based on log line content. */
const getLineColor = (raw) => {
  if (/is connected/i.test(raw)) return 'text-green-400';
  if (/has been disconnected/i.test(raw)) return 'text-red-400';
  if (/hit by|damage/i.test(raw)) return 'text-orange-400';
  if (/placed |packed /i.test(raw)) return 'text-blue-400';
  if (/##### PlayerList/i.test(raw) || raw === '#####') return 'text-gray-500';
  if (/AdminLog started/i.test(raw) || /^\*+/.test(raw)) return 'text-gray-500';
  return 'text-gray-300';
};

/** Converts a Date to a local datetime-local input value (YYYY-MM-DDTHH:mm). */
const toLocalInput = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

function ServerLogsPage() {
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fetched, setFetched] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => toLocalInput(new Date(Date.now() - 24 * 60 * 60 * 1000)));
  const [dateTo, setDateTo] = useState(() => toLocalInput(new Date()));

  // Fetch current user for Layout
  useEffect(() => {
    if (!BACKEND_API_URL) return;
    fetch(`${BACKEND_API_URL}/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.success && data.user) setUser(data.user); })
      .catch(e => console.error('Failed to fetch current user', e));
  }, []);

  const fetchLogs = useCallback(async () => {
    if (!BACKEND_API_URL) {
      setError('Backend API URL is not configured.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fromUnix = Math.floor(new Date(dateFrom).getTime() / 1000);
      const toUnix = Math.floor(new Date(dateTo).getTime() / 1000);
      const response = await fetch(`${BACKEND_API_URL}/api/admin/logs?from=${fromUnix}&to=${toUnix}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      const result = await response.json();
      if (result.success && result.data) {
        setFiles(result.data.files || []);
        setLogs(result.data.logs || []);
        setFetched(true);
      } else {
        throw new Error(result.message || 'Failed to fetch log data.');
      }
    } catch (err) {
      console.error('Error fetching server logs:', err);
      setError(err.message);
      setLogs([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  // Client-side search filter
  const filteredLogs = searchTerm
    ? logs.filter(l => l.raw.toLowerCase().includes(searchTerm.toLowerCase()))
    : logs;

  // Build a lookup: filename → modified_at string from the files array
  const fileDateMap = {};
  for (const f of files) {
    fileDateMap[f.name] = f.modified_at;
  }

  return (
    <Layout user={user}>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-semibold mb-6 text-center flex items-center justify-center gap-3">
          <FaClipboardList className="text-blue-400" /> Server Logs
        </h1>

        {error && (
          <div className="bg-red-900 bg-opacity-40 border border-red-600 text-red-300 px-4 py-3 rounded-lg relative mb-4" role="alert">
            {error}
          </div>
        )}

        {/* Initial state — hero card before fetching */}
        {!fetched && !loading && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-800 bg-opacity-75 rounded-xl border border-gray-700 p-8 text-center">
              <FaClipboardList className="text-blue-400 text-5xl mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-200 mb-2">DayZ Admin Logs</h2>
              <p className="text-gray-400 mb-6 text-sm">
                Fetch and search server admin log files (.ADM) from Nitrado.
              </p>

              {/* Date range picker */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6 justify-center items-center">
                <div className="flex flex-col items-start">
                  <label className="text-xs text-gray-500 mb-1">From</label>
                  <input
                    type="datetime-local"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="bg-gray-700 border border-gray-600 text-gray-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="flex flex-col items-start">
                  <label className="text-xs text-gray-500 mb-1">To</label>
                  <input
                    type="datetime-local"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="bg-gray-700 border border-gray-600 text-gray-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={fetchLogs}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors text-sm"
              >
                Fetch Logs
              </button>

              {/* Legend */}
              <div className="mt-8 border-t border-gray-700 pt-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Log Color Legend</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-green-400">
                    <FaUserCheck className="flex-shrink-0" /> <span>Connected</span>
                  </div>
                  <div className="flex items-center gap-2 text-red-400">
                    <FaUserTimes className="flex-shrink-0" /> <span>Disconnected</span>
                  </div>
                  <div className="flex items-center gap-2 text-orange-400">
                    <FaCrosshairs className="flex-shrink-0" /> <span>Hit / Damage</span>
                  </div>
                  <div className="flex items-center gap-2 text-blue-400">
                    <FaCampground className="flex-shrink-0" /> <span>Placed / Packed</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <FaUsers className="flex-shrink-0" /> <span>Player List</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <FaSearch className="flex-shrink-0" /> <span>Other Events</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-800 bg-opacity-75 rounded-xl border border-gray-700 p-10 text-center">
              <div className="inline-block w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-gray-300 font-semibold">Fetching log files...</p>
              <p className="text-gray-500 text-sm mt-1">Downloading .ADM files from Nitrado</p>
            </div>
          </div>
        )}

        {/* Fetched state — controls + log viewer */}
        {fetched && !loading && (
          <>
            {/* Controls bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4 items-end">
              <div className="flex flex-col items-start">
                <label className="text-xs text-gray-500 mb-1">From</label>
                <input
                  type="datetime-local"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-gray-800 border border-gray-600 text-gray-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex flex-col items-start">
                <label className="text-xs text-gray-500 mb-1">To</label>
                <input
                  type="datetime-local"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="bg-gray-800 border border-gray-600 text-gray-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <button
                onClick={fetchLogs}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-5 rounded-md transition-colors text-sm flex-shrink-0"
              >
                Refresh Logs
              </button>

              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 text-gray-200 rounded-md py-2 px-4 pl-10 text-sm focus:outline-none focus:border-blue-500"
                />
                <FaSearch className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* File summary */}
            {files.length > 0 && (
              <div className="bg-gray-800 bg-opacity-75 rounded-lg p-3 mb-4 text-sm text-gray-400">
                <span className="font-semibold text-gray-300">{files.length} log file{files.length !== 1 ? 's' : ''}</span> loaded
                <span className="text-gray-500 ml-1">
                  ({logs.length} lines, {new Date(files[0].modified_at).toLocaleString()} — {new Date(files[files.length - 1].modified_at).toLocaleString()})
                </span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <span key={i} className="inline-block bg-gray-700 rounded px-2 py-0.5 text-xs text-gray-300">
                      {f.name} <span className="text-gray-500">({(f.size / 1024).toFixed(1)} KB)</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Result count when searching */}
            {searchTerm && (
              <div className="text-sm text-gray-400 mb-2">
                Showing {filteredLogs.length} of {logs.length} lines matching "{searchTerm}"
              </div>
            )}

            {/* Log viewer */}
            <div className="bg-gray-900 bg-opacity-90 rounded-lg border border-gray-700 overflow-hidden">
              <div className="overflow-y-auto max-h-[65vh]">
                {filteredLogs.length > 0 ? (
                  <table className="w-full">
                    <tbody className="font-mono text-xs leading-relaxed">
                      {filteredLogs.map((line, i) => {
                        const prevSource = i > 0 ? filteredLogs[i - 1].source : null;
                        const showHeader = line.source !== prevSource;
                        const fileDate = fileDateMap[line.source];
                        return (
                          <React.Fragment key={i}>
                            {showHeader && (
                              <tr className="bg-gray-700 bg-opacity-60 border-b border-gray-600">
                                <td colSpan={2} className="px-3 py-1.5 text-xs font-sans">
                                  <span className="font-semibold text-yellow-400">{line.source}</span>
                                  {fileDate && (
                                    <span className="text-gray-400 ml-2">
                                      {new Date(fileDate).toLocaleString()}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            )}
                            <tr className="hover:bg-gray-800 border-b border-gray-800">
                              <td className="px-2 py-0.5 text-gray-600 text-right select-none w-12 align-top flex-shrink-0">
                                {i + 1}
                              </td>
                              <td className={`px-3 py-0.5 whitespace-pre-wrap break-all ${getLineColor(line.raw)}`}>
                                {line.raw}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-10 text-gray-500">
                    {searchTerm ? 'No matching log lines found.' : 'No log lines found for the selected date range.'}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default ServerLogsPage;
